/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * RENTAL DATA MODEL REFACTORING - STRUCTURED DATA APPROACH
 *
 * BREAKING CHANGE: Rental data is now sent as structured fields instead of concatenated strings
 *
 * BEFORE (Anti-pattern):
 * - unit, tenant, startDate, paymentDay were concatenated into a single `fullDescription` string
 * - Made querying/filtering impossible
 * - Required string parsing for data extraction
 * - Poor data integrity and scalability
 * - No type safety or validation
 *
 * AFTER (Best Practice):
 * - Each field is sent separately to the backend as typed, validated data
 * - Backend stores each field in dedicated database columns
 * - Enables proper querying, filtering, sorting, and reporting
 * - Maintains data integrity and type safety
 * - Supports advanced analytics and business intelligence
 *
 * DATA FLOW ARCHITECTURE:
 * ======================
 * 1. Frontend Form: Collects structured data with validation
 * 2. Smart Contract: Receives minimal required parameters for blockchain
 * 3. Backend API: Receives all structured fields for proper storage and validation
 * 4. Database: Stores each field in dedicated, indexed columns
 * 5. Analytics: Enables complex queries and reporting on rental data
 *
 * BACKEND DATABASE SCHEMA REQUIREMENTS:
 * ====================================
 * The `/rental/create` or equivalent API endpoint must be updated to accept:
 * - Individual fields instead of `rentalDescription` containing concatenated data
 * - Proper database schema with separate columns for each field:
 *   - unit: VARCHAR (for filtering by unit/floor)
 *   - tenant: VARCHAR (for tenant management and reporting)
 *   - startDate: DATE (for lease management and queries)
 *   - paymentDay: INT (for payment scheduling, 1-31)
 *   - beds, baths: INT (for property specifications)
 *   - sqft: INT (for property size queries)
 *   - monthlyRent: DECIMAL (for financial reporting)
 *   - duration, durationUnit: INT (for lease term calculations)
 *   - allowPayRent: BOOLEAN/INT (for payment permissions)
 * - Validation for date formats, numeric ranges, and business rules
 * - Foreign key relationships and proper indexing
 *
 * QUERYING BENEFITS:
 * =================
 * - Filter rentals by date ranges: WHERE startDate BETWEEN '2024-01-01' AND '2024-12-31'
 * - Find properties by size: WHERE sqft >= 1000 AND beds >= 2
 * - Payment scheduling: WHERE paymentDay = 1 (first of month)
 * - Financial reporting: SUM(monthlyRent) GROUP BY tenant
 * - Lease management: WHERE durationUnit = 1 AND duration >= 12 (yearly leases)
 */

import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { actions } from '../../actions';
import { services } from '../../services';
import { createRental, sendTransactionHash } from '../../services/transactionApi';
import { createAdminNotification } from '../../services/notificationService';
import { Web3Data } from '../../types/nft/create.rental';
import { web3 } from '../../web3';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../ui/use-toast';

interface AddRentalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: () => void; // Callback to refresh the rental list
}

/**
 * Rental Form Data Interface
 *
 * IMPORTANT: Each field is stored as a separate, queryable attribute
 * in the backend database. This provides better data integrity, query performance,
 * and UI flexibility compared to concatenating structured data into strings.
 *
 * Data Flow:
 * 1. Form collects structured data
 * 2. Smart contract receives minimal required parameters
 * 3. Backend API receives all structured fields for proper storage
 * 4. Database stores each field in dedicated columns/tables
 */
export interface RentalFormData {
  property: string; // assetAddress - Blockchain asset identifier
  assetName: string; // Display name for UI
  unit: string; // Unit/floor identifier - stored separately for filtering
  tenant: string; // Tenant identifier/name - stored separately for management
  monthlyRent: number; // Monthly rent amount in ether
  startDate: string; // Lease start date - stored separately for date queries
  paymentDay: number; // Payment day of month (1-31) - stored separately for scheduling
  duration: number; // Rental duration value
  durationUnit: number; // 0=Days, 1=Months, 2=Years
  allowPayRent: number; // 0=Admin, 1=Tenant - payment permission setting
  rentalDescription: string; // Free-form description text
  beds: number; // Number of bedrooms
  baths: number; // Number of bathrooms
  sqft: number; // Square footage
}

interface Asset {
  _id: string;
  assetAddress: string;
  assetName: string;
  status: string;
  saleEndDate?: string;
}

export function AddRentalModal({ open, onOpenChange, onAdd }: AddRentalModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<RentalFormData>({
    property: '',
    assetName: '',
    unit: '',
    tenant: '',
    monthlyRent: 0,
    startDate: '',
    paymentDay: 1,
    duration: 30,
    durationUnit: 0, // Days
    allowPayRent: 0, // Admin
    rentalDescription: '',
    beds: 0,
    baths: 0,
    sqft: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [rentalContractInstance, setRentalContractInstance] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch available assets
  useEffect(() => {
    const fetchAssets = async () => {
      if (!open) return;

      setLoadingAssets(true);
      try {
        // Use the public /asset/user endpoint: /asset requires admin auth and
        // returned 401 (empty dropdown). This endpoint returns APPROVED assets.
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/asset/user?page=1&limit=100`
        );
        const result = await response.json();

        if (result.data && Array.isArray(result.data)) {
          // Only assets whose sale period has ENDED are rentable on-chain. The DB
          // status enum is only PENDING/APPROVED/REJECTED (never ACTIVE/SOLDOUT),
          // so the old status filter matched nothing — gate on saleEndDate instead.
          const now = new Date();
          const availableAssets = result.data.filter(
            (asset: Asset) =>
              asset.assetAddress && // skip records not yet deployed on-chain
              asset.saleEndDate &&
              new Date(asset.saleEndDate) < now
          );
          setAssets(availableAssets);
        }
      } catch (error) {
        toast({
          description: 'Failed to load assets',
          variant: 'destructive',
        });
      } finally {
        setLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [open, toast]);

  // Initialize Web3 and contracts
  useEffect(() => {
    if (!open) return;

    const initializeWeb3 = async () => {
      try {
        const web3Data = await services.getWeb3();
        if (web3Data) {
          setWeb3Data(web3Data as Web3Data);
        }
      } catch (error) {
        setError('Failed to initialize Web3. Please check your wallet connection.');
      }
    };

    const initializeContract = async () => {
      try {
        const instance = await actions.getRentalPoolContract();
        setRentalContractInstance(instance);
      } catch (error) {
        setError('Failed to initialize rental contract. Please try again later.');
      }
    };

    initializeWeb3();
    initializeContract();
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.property || !formData.monthlyRent || !formData.duration) {
      toast({
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (!web3Data || !web3Data.accounts || web3Data.accounts.length === 0) {
      toast({
        description: 'Web3 wallet not connected. Please connect your wallet.',
        variant: 'destructive',
      });
      return;
    }

    if (!rentalContractInstance) {
      toast({
        description: 'Rental contract not initialized. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Verify owner account
      const owner = await rentalContractInstance.methods.owner().call();
      if (owner.toLowerCase() !== web3Data.accounts[0].toLowerCase()) {
        toast({
          description: "You haven't logged in with the owner account.",
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Check if property is already listed
      try {
        const isAlreadyListed = await rentalContractInstance.methods
          .listedProperties(formData.property)
          .call();
        if (isAlreadyListed) {
          toast({
            description: 'This property is already listed for rent.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }
      } catch (checkErr) {
        console.warn('Could not check listedProperties status:', checkErr);
      }

      // Check if property is enabled/registered on the rental contract (isPropertyListed)
      try {
        const isRegisteredOnRental = await rentalContractInstance.methods
          .isPropertyListed(formData.property)
          .call();

        if (!isRegisteredOnRental) {
          toast({
            description: 'Property not yet enabled on rental contract. Please approve in MetaMask to enable it...',
            variant: 'default',
          });

          await rentalContractInstance.methods
            .setPropertyAddress(formData.property)
            .send({
              from: web3Data.accounts[0],
              gas: 500000,
              gasPrice: web3.utils.toWei('20', 'gwei'),
            });

          toast({
            description: 'Property enabled on rental contract! Now submitting rental listing...',
            variant: 'default',
          });
        }
      } catch (enableErr: any) {
        console.error('Failed to enable property on rental contract:', enableErr);
        let errMsg = enableErr?.data?.message || enableErr?.message || 'Failed to enable property on rental contract';
        errMsg = errMsg.replace(/: 0x[0-9a-fA-F]+/g, '').trim();
        toast({
          description: `Cannot enable property: ${errMsg}`,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Convert monthly rent to wei (assuming monthlyRent is in ether)
      const priceInWei = web3.utils.toWei(formData.monthlyRent.toString(), 'ether');

      // Pre-flight simulation check to detect contract reverts before sending transaction
      try {
        await rentalContractInstance.methods
          .listPropertyForRent(
            formData.property,
            formData.duration,
            formData.durationUnit,
            priceInWei,
            formData.allowPayRent
          )
          .call({ from: web3Data.accounts[0] });
      } catch (simErr: any) {
        console.error('Contract simulation failed:', simErr);
        let errMsg = simErr?.data?.message || simErr?.message || 'Smart contract rejected the transaction';
        errMsg = errMsg.replace(/: 0x[0-9a-fA-F]+/g, '').trim();
        toast({
          description: `Contract error: ${errMsg}`,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Estimate gas with safe fallback cap (BSC node cap is 16,777,216)
      let gasLimit = 3000000;
      try {
        const estimatedGas = await rentalContractInstance.methods
          .listPropertyForRent(
            formData.property,
            formData.duration,
            formData.durationUnit,
            priceInWei,
            formData.allowPayRent
          )
          .estimateGas({ from: web3Data.accounts[0] });
        gasLimit = Math.min(Math.floor(Number(estimatedGas) * 1.3), 3000000);
      } catch (gasErr) {
        console.warn('Gas estimation failed, using safe gas limit 3000000:', gasErr);
        gasLimit = 3000000;
      }

      // Call smart contract to list property for rent
      await rentalContractInstance.methods
        .listPropertyForRent(
          formData.property,
          formData.duration,
          formData.durationUnit,
          priceInWei,
          formData.allowPayRent
        )
        .send({
          from: web3Data.accounts[0],
          gas: gasLimit,
          gasPrice: web3.utils.toWei('20', 'gwei'),
        })
        .on('transactionHash', async (hash: string) => {
          try {
            await sendTransactionHash(hash);
          } catch (apiError) {
            // Transaction hash recording failed, but transaction is still valid
          }
        })
        .on('receipt', async (receipt: any) => {
          toast({
            description: 'Rental listed successfully on blockchain',
            variant: 'default',
          });
          createAdminNotification({
            type: 'transaction',
            title: 'Rental listed',
            message: `New rental listing was added on blockchain.`,
            priority: 'medium',
          });

          // Wait a bit for the backend to process the event
          await new Promise((resolve) => setTimeout(resolve, 2000));

          /**
           * BACKEND API PAYLOAD - STRUCTURED DATA APPROACH
           *
           * Benefits of this approach:
           * ✅ Individual fields can be queried/filtered independently
           * ✅ Better data validation and type safety
           * ✅ Easier UI rendering and form population
           * ✅ Scalable for future requirements
           * ✅ Maintains data integrity (no string parsing needed)
           *
           * Backend should store each field in separate database columns:
           * - rentalDescription: TEXT
           * - unit: VARCHAR(255) NULL
           * - tenant: VARCHAR(255) NULL
           * - startDate: DATE NULL
           * - paymentDay: INT NULL
           * - beds, baths, sqft: INT
           * - monthlyRent, duration: DECIMAL/INT
           * - durationUnit, allowPayRent: INT
           */

          // Validate structured data before sending to backend
          const validationErrors: string[] = [];

          // Required numeric validations
          if (formData.beds < 0) validationErrors.push('Beds cannot be negative');
          if (formData.baths < 0) validationErrors.push('Baths cannot be negative');
          if (formData.sqft <= 0) validationErrors.push('Square footage must be positive');
          if (formData.monthlyRent <= 0) validationErrors.push('Monthly rent must be positive');
          if (formData.duration <= 0) validationErrors.push('Duration must be positive');

          // Date validation
          if (formData.startDate && isNaN(Date.parse(formData.startDate))) {
            validationErrors.push('Invalid start date format');
          }

          // Payment day validation
          if (
            formData.paymentDay !== null &&
            (formData.paymentDay < 1 || formData.paymentDay > 31)
          ) {
            validationErrors.push('Payment day must be between 1 and 31');
          }

          // Duration unit validation
          if (![0, 1, 2].includes(formData.durationUnit)) {
            validationErrors.push('Invalid duration unit');
          }

          // Allow pay rent validation
          if (![0, 1].includes(formData.allowPayRent)) {
            validationErrors.push('Invalid payment permission setting');
          }

          if (validationErrors.length > 0) {
            console.error('Data validation errors:', validationErrors);
            toast({
              description: `Data validation failed: ${validationErrors.join(', ')}`,
              variant: 'destructive',
            });
            return;
          }

          const payload = {
            txHash: receipt.transactionHash,
            rentalDescription: formData.rentalDescription,
            unit: formData.unit || null,
            tenant: formData.tenant || null,
            startDate: formData.startDate || null,
            paymentDay: formData.paymentDay || null,
            beds: formData.beds,
            baths: formData.baths,
            sqft: formData.sqft,
            monthlyRent: formData.monthlyRent,
            duration: formData.duration,
            durationUnit: formData.durationUnit,
            allowPayRent: formData.allowPayRent,
            // Add metadata for better tracking
            createdAt: new Date().toISOString(),
            blockchainTxHash: receipt.transactionHash,
            propertyAddress: formData.property,
          };

          try {
            console.log('Sending structured rental data to backend:', payload);
            await createRental(payload);
            console.log('Rental metadata saved successfully');
            toast({
              description: 'Rental details saved successfully',
              variant: 'default',
            });
          } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            console.error('Failed to save rental metadata:', errorMessage);
            toast({
              description: `Rental listed on blockchain but failed to save details: ${errorMessage}`,
              variant: 'destructive',
            });
          }

          // Reset form
          setFormData({
            property: '',
            assetName: '',
            unit: '',
            tenant: '',
            monthlyRent: 0,
            startDate: '',
            paymentDay: 1,
            duration: 30,
            durationUnit: 0,
            allowPayRent: 0,
            rentalDescription: '',
            beds: 0,
            baths: 0,
            sqft: 0,
          });

          setIsSubmitting(false);
          onOpenChange(false);
          onAdd(); // Refresh rental list
        })
        .on('error', async (error: any) => {
          toast({
            description: error.message || 'Rental transaction failed',
            variant: 'destructive',
          });
          setIsSubmitting(false);
        });
    } catch (error: any) {
      setError(error.message || 'Error creating rental');
      toast({
        description: error.message || 'Error creating rental',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  const handlePropertyChange = (assetAddress: string) => {
    const selectedAsset = assets.find((asset) => asset.assetAddress === assetAddress);
    setFormData({
      ...formData,
      property: assetAddress,
      assetName: selectedAsset?.assetName || '',
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        property: '',
        assetName: '',
        unit: '',
        tenant: '',
        monthlyRent: 0,
        startDate: '',
        paymentDay: 1,
        duration: 30,
        durationUnit: 0,
        allowPayRent: 0,
        rentalDescription: '',
        beds: 0,
        baths: 0,
        sqft: 0,
      });
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[500px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Add New Rental</DialogTitle>
          <DialogDescription>
            List a property for rent on the blockchain. This will create a rental listing that can
            be rented by tenants.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className='p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg'>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='property'>Property *</Label>
            {loadingAssets ? (
              <div className='flex items-center justify-center p-4'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='ml-2 text-sm text-muted-foreground'>Loading assets...</span>
              </div>
            ) : (
              <Select
                value={formData.property}
                onValueChange={handlePropertyChange}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select property' />
                </SelectTrigger>
                <SelectContent>
                  {assets.map((asset) => (
                    <SelectItem key={asset._id} value={asset.assetAddress}>
                      {asset.assetName} ({asset.assetAddress?.slice(0, 6)}...
                      {asset.assetAddress?.slice(-4)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='unit'>Unit/Floor</Label>
              <Input
                id='unit'
                placeholder='e.g., Unit 4A, Floor 12'
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='tenant'>Tenant Name</Label>
              <Input
                id='tenant'
                placeholder='Enter tenant name or company'
                value={formData.tenant}
                onChange={(e) => setFormData({ ...formData, tenant: e.target.value })}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='monthlyRent'>Monthly Rent (ETH) *</Label>
              <Input
                id='monthlyRent'
                type='number'
                step='0.0001'
                placeholder='0.5'
                value={formData.monthlyRent || ''}
                onChange={(e) => setFormData({ ...formData, monthlyRent: Number(e.target.value) })}
                disabled={isSubmitting}
                required
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='paymentDay'>Payment Day</Label>
              <Select
                value={String(formData.paymentDay)}
                onValueChange={(value) => setFormData({ ...formData, paymentDay: Number(value) })}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 5, 10, 15, 20, 25].map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      {day}th of month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='duration'>Rental Duration *</Label>
              <Input
                id='duration'
                type='number'
                placeholder='30'
                value={formData.duration}
                onChange={(e) =>
                  setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })
                }
                disabled={isSubmitting}
                required
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='durationUnit'>Duration Unit *</Label>
              <Select
                value={String(formData.durationUnit)}
                onValueChange={(value) =>
                  setFormData({ ...formData, durationUnit: parseInt(value) })
                }
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='0'>Days</SelectItem>
                  <SelectItem value='1'>Months</SelectItem>
                  <SelectItem value='2'>Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='allowPayRent'>Rental Distributor *</Label>
            <Select
              value={String(formData.allowPayRent)}
              onValueChange={(value) => setFormData({ ...formData, allowPayRent: parseInt(value) })}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='1'>Tenant</SelectItem>
                <SelectItem value='0'>Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='rentalDescription'>Rental Description *</Label>
            <Input
              id='rentalDescription'
              placeholder='Enter rental description'
              value={formData.rentalDescription}
              onChange={(e) => setFormData({ ...formData, rentalDescription: e.target.value })}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className='grid grid-cols-3 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='beds'>Beds</Label>
              <Input
                id='beds'
                type='number'
                value={formData.beds}
                onChange={(e) => setFormData({ ...formData, beds: parseInt(e.target.value) || 0 })}
                disabled={isSubmitting}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='baths'>Baths</Label>
              <Input
                id='baths'
                type='number'
                value={formData.baths}
                onChange={(e) => setFormData({ ...formData, baths: parseInt(e.target.value) || 0 })}
                disabled={isSubmitting}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='sqft'>Square Feet</Label>
              <Input
                id='sqft'
                type='number'
                value={formData.sqft}
                onChange={(e) => setFormData({ ...formData, sqft: parseInt(e.target.value) || 0 })}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='startDate'>Lease Start Date</Label>
            <Input
              id='startDate'
              type='date'
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter className='pt-4'>
            <Button type='button' variant='outline' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type='submit' variant='default' disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Creating...
                </>
              ) : (
                'Add Rental'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
